import { SignInIdentifier } from '@logto/schemas';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CaptchaBox from '@/containers/CaptchaBox';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useTerms from '@/hooks/use-terms';

import EvaAgreement from '../EvaAgreement';
import EvaInput from '../EvaInput';
import EvaPasswordInput from '../EvaPasswordInput';
import { isValidEvaPhoneNumber } from '../EvaPhoneCodeSignIn/phone-validation';

import styles from './index.module.scss';
import useEvaPasswordSignIn from './use-eva-password-sign-in';

const phoneCountryCallingCode = '86';
const agreementShakeDuration = 400;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 密码登录 tab(EVA 定制)。
 *
 * 错误提示刻意统一:「账号不存在」与「密码错误」在服务端同为
 * `session.invalid_credentials`,这里只展示服务端那句统一文案,
 * 不做任何区分类提示(防账号枚举,见 sso 设计文档第八节)。
 */
const EvaPasswordSignIn = () => {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [isAgreementShaking, setIsAgreementShaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { termsAgreement, isTermsDisabled } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();
  const { errorMessage, clearErrorMessage, onSubmit } = useEvaPasswordSignIn();

  const identifierType = useMemo(() => {
    if (identifier.includes('@')) {
      return SignInIdentifier.Email;
    }
    if (/^\d+$/.test(identifier)) {
      return SignInIdentifier.Phone;
    }
  }, [identifier]);

  const validateAgreement = useCallback(() => {
    if (isTermsDisabled || termsAgreement) {
      return true;
    }

    setIsAgreementShaking(true);
    window.setTimeout(() => {
      setIsAgreementShaking(false);
    }, agreementShakeDuration);

    return false;
  }, [isTermsDisabled, termsAgreement]);

  const validateInputs = useCallback(() => {
    if (!identifierType) {
      setLocalError(t('eva_domestic.invalid_identifier'));
      return false;
    }
    if (identifierType === SignInIdentifier.Email && !emailPattern.test(identifier)) {
      setLocalError(t('eva_domestic.invalid_identifier'));
      return false;
    }
    if (identifierType === SignInIdentifier.Phone && !isValidEvaPhoneNumber(identifier)) {
      setLocalError(t('eva_domestic.invalid_identifier'));
      return false;
    }
    if (!password) {
      setLocalError(t('eva_domestic.empty_password'));
      return false;
    }

    return true;
  }, [identifier, identifierType, password, t]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setLocalError('');
      clearErrorMessage();

      if (!validateAgreement() || !validateInputs() || !identifierType) {
        return;
      }

      setIsSubmitting(true);

      try {
        await onSubmit({
          identifier: {
            type: identifierType,
            value:
              identifierType === SignInIdentifier.Phone
                ? `${phoneCountryCallingCode}${identifier}`
                : identifier.trim(),
          },
          password,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      clearErrorMessage,
      identifier,
      identifierType,
      onSubmit,
      password,
      validateAgreement,
      validateInputs,
    ]
  );

  const displayError = localError || errorMessage;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.fields}>
        <EvaInput
          name="identifier"
          autoComplete="username"
          inputMode="email"
          placeholder={t('eva_domestic.identifier_placeholder')}
          value={identifier}
          isDanger={Boolean(localError)}
          onChange={(event) => {
            setIdentifier(event.currentTarget.value);
            setLocalError('');
            clearErrorMessage();
          }}
        />

        <div className={styles.passwordField}>
          <EvaPasswordInput
            name="password"
            autoComplete="current-password"
            placeholder={t('eva_domestic.empty_password')}
            value={password}
            isDanger={Boolean(displayError)}
            onChange={(event) => {
              setPassword(event.currentTarget.value);
              setLocalError('');
              clearErrorMessage();
            }}
          />
          {displayError && <div className={styles.error}>{displayError}</div>}
        </div>

        {/* 必须渲染:captcha 容器(widgetRef)由它提供,缺了 executeCaptcha 拿不到容器只会
            resolve(undefined),开着 captcha 策略的环境(如 dev/阿里云)会被服务端 422 拒掉 */}
        <CaptchaBox />

        <EvaAgreement className={styles.agreement} isShaking={isAgreementShaking} />
      </div>

      <button
        className={styles.submit}
        disabled={isSubmitting || !identifier || !password}
        type="submit"
      >
        {isSubmitting ? (
          <>
            <LoaderCircle aria-hidden className={styles.submitLoader} size={20} />
            <span>{t('eva_domestic.signing_in')}</span>
          </>
        ) : (
          t('eva_domestic.sign_in')
        )}
      </button>

      <button
        type="button"
        className={styles.forgotLink}
        onClick={() => {
          navigate('/forgot-password');
        }}
      >
        {t('eva_domestic.forgot_password')}
      </button>
    </form>
  );
};

export default EvaPasswordSignIn;
